import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { isValidPhone } from '../../utils/contact';

interface PracticeDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: {
        practiceId: string;
        name: string;
        phone: string;
        address?: string;
        city?: string;
        state?: string;
        zip?: string;
    }) => void;
    initial?: {
        practiceId?: string;
        name?: string;
        phone?: string;
        address?: string;
        city?: string;
        state?: string;
        zip?: string;
    };
}

const PracticeDetailsModal: React.FC<PracticeDetailsModalProps> = ({
    isOpen,
    onClose,
    onSave,
    initial
}) => {
    const [practiceId, setPracticeId] = useState('');
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zip, setZip] = useState('');

    const isValid = name.trim().length > 1 && isValidPhone(phone) && address.trim().length > 2;

    useEffect(() => {
        if (isOpen) {
            setPracticeId(initial?.practiceId || '');
            setName(initial?.name || '');
            setPhone(initial?.phone || '');
            setAddress(initial?.address || '');
            setCity(initial?.city || '');
            setState(initial?.state || '');
            setZip(initial?.zip || '');
        }
    }, [isOpen, initial]);

    if (!isOpen) return null;

    const modalContent = (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-lg shadow-xl w-full max-w-[520px] p-6 relative"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900">Edit practice details</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-800">
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M6 18L18 6M6 6l12 12"
                            ></path>
                        </svg>
                    </button>
                </div>

                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-gray-700">Practice name</label>
                        <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g., Bright Smiles Dental"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-gray-700">Phone number</label>
                        <PhoneInput
                            country={'us'}
                            onlyCountries={['us', 'ca']}
                            disableDropdown
                            countryCodeEditable={false}
                            value={(phone || '').replace(/^[+]/, '')}
                            onChange={(value) =>
                                setPhone(value.startsWith('+') ? value : `+${value}`)
                            }
                            inputStyle={{ width: '100%', height: '40px', borderRadius: '8px' }}
                            containerClass="w-full"
                            buttonClass="!border-white-300 !bg-white !rounded-l-lg !border-radius-xs"
                            inputClass={`${phone && !isValidPhone(phone) ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'} px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            placeholder="(201) 555-0123"
                        />
                        {phone && !isValidPhone(phone) && (
                            <p className="text-red-500 text-sm mt-1">
                                Please enter a valid US or Canadian phone number
                            </p>
                        )}
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-gray-700">Address</label>
                        <input
                            type="text"
                            placeholder="Street address"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <input
                            type="text"
                            placeholder="City"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="flex space-x-3">
                            <input
                                type="text"
                                placeholder="State"
                                value={state}
                                onChange={(e) => setState(e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <input
                                type="text"
                                placeholder="ZIP Code"
                                value={zip}
                                onChange={(e) => setZip(e.target.value)}
                                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                </div>

                <div className="flex justify-end items-center gap-3 mt-6">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-semibold text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        disabled={!isValid}
                        onClick={() =>
                            onSave({ practiceId, name, phone, address, city, state, zip })
                        }
                        className={`px-4 py-2 text-sm font-bold text-white rounded-lg ${isValid ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-300 cursor-not-allowed'}`}
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );

    const modalRoot = document.getElementById('modal-root');
    return modalRoot ? ReactDOM.createPortal(modalContent, modalRoot) : null;
};

export default PracticeDetailsModal;
